/**
 * Canonical chat app theme IDs + swatches.
 * Consumed by web, parent (appearance sync), and theme-init allowlist tests.
 * Admin/report keep their own design systems on purpose.
 */
export const APP_THEME_IDS = [
  'aurora',
  'neon-terminal',
  'synthwave',
  'ocean',
  'forest',
  'sunset-paper',
  'lunar',
  'infernal',
  'corporate-slate',
  'finance-charcoal',
  'clinical',
] as const;

export type AppThemeId = (typeof APP_THEME_IDS)[number];

export const DEFAULT_APP_THEME_ID: AppThemeId = 'aurora';

export const APP_THEME_STORAGE_KEY = 'appTheme';

/** Comma-separated allowlist for pre-paint `theme-init.js` (must stay in sync). */
export const APP_THEME_IDS_CSV = APP_THEME_IDS.join(',');

export type AppThemeGroup = 'expressive' | 'professional';

export const APP_THEME_OPTIONS: {
  id: AppThemeId;
  label: string;
  group: AppThemeGroup;
  /** Mini preview swatch (smooth blend) when `data-bs-theme="light"`. */
  swatchBackgroundLight: string;
  /** Mini preview swatch (smooth blend) when `data-bs-theme="dark"`. */
  swatchBackgroundDark: string;
}[] = [
  {
    id: 'aurora',
    label: 'Aurora glass',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.964 0.013 247) 0%, oklch(0.9 0.05 200) 45%, oklch(0.77 0.118 180) 88%, oklch(0.71 0.11 181) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.205 0.03 250) 0%, oklch(0.32 0.05 248) 45%, oklch(0.78 0.118 180) 88%, oklch(0.84 0.1 182) 100%)',
  },
  {
    id: 'neon-terminal',
    label: 'Neon terminal',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.94 0.012 165) 0%, oklch(0.82 0.06 165) 40%, oklch(0.52 0.19 165) 85%, oklch(0.35 0.15 168) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.16 0.025 165) 0%, oklch(0.26 0.04 165) 40%, oklch(0.78 0.2 158) 85%, oklch(0.52 0.14 158) 100%)',
  },
  {
    id: 'synthwave',
    label: 'Synthwave dusk',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.955 0.028 320) 0%, oklch(0.88 0.09 310) 38%, oklch(0.58 0.22 330) 78%, oklch(0.45 0.2 305) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.18 0.06 290) 0%, oklch(0.28 0.08 295) 38%, oklch(0.72 0.22 330) 78%, oklch(0.52 0.16 305) 100%)',
  },
  {
    id: 'ocean',
    label: 'Ocean depth',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.97 0.018 230) 0%, oklch(0.52 0.12 210) 52%, oklch(0.72 0.14 25) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.2 0.05 240) 0%, oklch(0.34 0.06 242) 52%, oklch(0.72 0.14 25) 100%)',
  },
  {
    id: 'forest',
    label: 'Forest night',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.965 0.022 145) 0%, oklch(0.89 0.04 138) 45%, oklch(0.48 0.12 145) 92%, oklch(0.36 0.1 150) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.19 0.04 145) 0%, oklch(0.3 0.05 145) 45%, oklch(0.74 0.1 150) 92%, oklch(0.55 0.08 152) 100%)',
  },
  {
    id: 'sunset-paper',
    label: 'Sunset paper',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.975 0.025 55) 0%, oklch(0.92 0.06 48) 40%, oklch(0.56 0.16 25) 85%, oklch(0.78 0.14 75) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.2 0.03 55) 0%, oklch(0.32 0.045 50) 40%, oklch(0.78 0.14 75) 85%, oklch(0.58 0.12 78) 100%)',
  },
  {
    id: 'lunar',
    label: 'Lunar minimal',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.97 0.008 270) 0%, oklch(0.9 0.02 268) 50%, oklch(0.54 0.12 250) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.22 0.02 270) 0%, oklch(0.34 0.025 268) 50%, oklch(0.72 0.1 280) 100%)',
  },
  {
    id: 'infernal',
    label: 'Infernal',
    group: 'expressive',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.1 0.03 25) 0%, oklch(0.28 0.14 27) 38%, oklch(0.52 0.24 27) 72%, oklch(0.55 0.2 48) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.06 0.02 25) 0%, oklch(0.22 0.12 27) 38%, oklch(0.55 0.24 27) 72%, oklch(0.6 0.2 48) 100%)',
  },
  {
    id: 'corporate-slate',
    label: 'Corporate slate',
    group: 'professional',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.995 0.003 252) 0%, oklch(0.96 0.008 252) 42%, oklch(0.72 0.09 255) 78%, oklch(0.5 0.14 255) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.22 0.02 252) 0%, oklch(0.3 0.022 252) 42%, oklch(0.52 0.09 255) 78%, oklch(0.65 0.12 255) 100%)',
  },
  {
    id: 'finance-charcoal',
    label: 'Finance charcoal',
    group: 'professional',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(0.995 0.005 85) 0%, oklch(0.94 0.012 82) 40%, oklch(0.65 0.08 75) 78%, oklch(0.52 0.09 75) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.2 0.018 85) 0%, oklch(0.28 0.02 82) 40%, oklch(0.58 0.1 82) 75%, oklch(0.72 0.12 85) 100%)',
  },
  {
    id: 'clinical',
    label: 'Clinical light',
    group: 'professional',
    swatchBackgroundLight:
      'linear-gradient(145deg, oklch(1 0 0) 0%, oklch(0.97 0.004 220) 35%, oklch(0.85 0.04 210) 72%, oklch(0.5 0.1 200) 100%)',
    swatchBackgroundDark:
      'linear-gradient(145deg, oklch(0.22 0.012 220) 0%, oklch(0.3 0.015 220) 38%, oklch(0.48 0.06 205) 72%, oklch(0.7 0.08 200) 100%)',
  },
];

export function isAppThemeId(value: string | null | undefined): value is AppThemeId {
  return value != null && (APP_THEME_IDS as readonly string[]).includes(value);
}
